import React, {
  createContext,
  ReactChild,
  ReactElement,
  useReducer,
  useState,
  useEffect
} from 'react';
import { getResources } from '../constants/resources';
import { ProofOfWorkState } from './state';
import proofOfWorkStateReducer from './stateReducer';
import { RequestService } from '../../../../common/request';
import { ProofOfWorkAction } from './action';
import {
  OnChallengeCompletedCallback,
  OnChallengeInvalidatedCallback,
  OnModalChallengeAbandonedCallback
} from '../interface';

export type ProofOfWorkContext = {
  state: ProofOfWorkState;
  dispatch: React.Dispatch<ProofOfWorkAction>;
};

export const ProofOfWorkContext = createContext<ProofOfWorkContext | null>(null);

type Props = {
  sessionId: string;
  requestService: RequestService;
  onChallengeCompleted: OnChallengeCompletedCallback;
  onChallengeInvalidated: OnChallengeInvalidatedCallback;
  onModalChallengeAbandoned: OnModalChallengeAbandonedCallback;
  children: ReactChild;
};

/**
 * A React provider is a special component that wraps a tree of components and
 * exposes some global state (context) to the entire tree. Descendants can then
 * access this context with `useContext`.
 */
export const ProofOfWorkContextProvider = ({
  sessionId,
  requestService,
  onChallengeCompleted,
  onChallengeInvalidated,
  onModalChallengeAbandoned,
  children
}: Props): ReactElement => {
  // We declare these variables as lazy-initialized state variables since they
  // do not need to be re-computed if this component re-renders.
  const [resources] = useState(() => getResources());
  const [initialState] = useState<ProofOfWorkState>(() => ({
    // Immutable parameters:
    sessionId,
    // Immutable state
    resources,
    requestService,
    onModalChallengeAbandoned,
    // Mutable state:
    onChallengeCompletedData: null,
    onChallengeInvalidatedData: null
  }));

  // Components will access and mutate state via these variables:
  const [state, dispatch] = useReducer(proofOfWorkStateReducer, initialState);

  // Completion effect:
  useEffect(() => {
    // Ensure that invalidation effect has not already fired.
    if (state.onChallengeCompletedData === null || state.onChallengeInvalidatedData !== null) {
      return;
    }

    onChallengeCompleted(state.onChallengeCompletedData);
  }, [state.onChallengeCompletedData, state.onChallengeInvalidatedData, onChallengeCompleted]);

  // Invalidation effect:
  useEffect(() => {
    // Ensure that completion effect has not already fired.
    if (state.onChallengeInvalidatedData === null || state.onChallengeCompletedData !== null) {
      return;
    }

    onChallengeInvalidated(state.onChallengeInvalidatedData);
  }, [state.onChallengeCompletedData, state.onChallengeInvalidatedData, onChallengeInvalidated]);

  /*
   * Effects
   *
   * NOTE: These effects cannot go inside the reducer, since reducers must not
   * have side-effects with respect to the app state.
   */

  return (
    <ProofOfWorkContext.Provider value={{ state, dispatch }}>
      {children}
    </ProofOfWorkContext.Provider>
  );
};
